import { useEffect, useRef, useState } from "react";
import { Code, ConnectError } from "@connectrpc/connect";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Flag,
  HelpCircle,
  Save,
  Send,
} from "lucide-react";
import {
  Badge,
  Button,
  Textarea,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@hub/ui";
import { CaseDocumentPane } from "./caseDocumentPane";
import type { ViewPrimitive, BladeContext } from "../../shell/registry";
import type { BladeDescriptor } from "../../shell/types";
import {
  BladeLoading,
  BladeError,
  BladeDenied,
  BladeEmpty,
  forcedBladeState,
} from "../../shell/BladeStates";
import {
  useAuditBatch,
  useAuditCase,
  useAuditSubmit,
} from "../AuditDataSource";
import { useTourNavigator } from "../help/TourProvider";
import { AUDIT_FIELDS, AUDIT_QUALIFIERS } from "../data/fields";
import {
  caseLabel,
  caseLabelTitle,
  isFallbackLabel,
} from "../data/caseIdentity";
import { coderById } from "../data/coders";
import type {
  AuditBatchCaseSummary,
  AuditCase,
  AuditField,
  CaseDisposition,
  CodedFields,
  VerdictId,
} from "../types";
import {
  CodeCell,
  CompareValue,
  FieldHelp,
  FieldStatusBadge,
  SLOT_ACCENT,
  SlotEyebrow,
  SlotRow,
  SrcTag,
  slotsMatch,
  useElementWidth,
} from "./compareParts";
import { CaseIdentifier, Progress as ProgressBar } from "./parts";
import {
  AddCodeControl,
  AddedCodeRow,
  CodebookModal,
  CorrectCodeEntry,
  CorrectToggle,
  FieldNote,
  MultiCodeRow,
  NeitherToggle,
  NoteButton,
  QualifierControl,
  ReasonSelect,
  SlotTag,
  VerdictChip,
  reasonLabel,
} from "./compareAdjudication";
import type {
  CaseVerdicts,
  MultiFieldVerdict,
  MultiRowVerdict,
  QualifierVerdict,
  SingleFieldVerdict,
} from "../data/verdicts";
import {
  buildFieldVerdicts,
  computeProgress,
  getDisposition,
  loadGeneralNote,
  loadSubmittedIds,
  loadVerdicts,
  setDisposition,
  multiFieldComplete,
  multiRowCorrect,
  multiUnionRows,
  reasonOptional,
  saveGeneralNote,
  saveVerdicts,
  singleFieldResolved,
  toggleNeither as computeToggleNeither,
  toggleSingleSlot,
} from "../data/verdicts";

// Audit compare + adjudication screen (HUB-504, S4–S7) — opened full-bleed in the
// shell's focus presentation mode. Left pane = the anesthesia-record document viewer
// (placeholder this slice); right pane = the field-by-field A/B comparison, now
// FULLY adjudicable: per-field Correct/Neither verdicts + reason + note, a codebook
// picker on "Neither", per-code review for multi-value fields, qualifier on/off
// correction, a general case note, and a Submit & next gate that auto-advances to the
// next unreviewed case. Verdict drafts persist to localStorage (a live mutation lands
// with the backend). Ported from the prototype's AuditReview/FieldCompare/
// MultiFieldCompare/QualifierStrip.
//
// BLIND INVARIANT: the case arrives via useAuditCase → the blinded AuditCase with
// opaque results.a / results.b and NO provenance. "Result A"/"Result B" are display
// slots; every verdict keys on the blind slots. Nothing maps a slot to agent/coder.

const SIDE_COLS =
  "minmax(150px,190px) minmax(0,1fr) minmax(0,1fr) minmax(200px,240px)";
const SIDE_THRESHOLD = 720;
const TWO_PANE_THRESHOLD = 900;

/** Case-status badge per disposition (drives the header pill). */
const DISPOSITION_BADGE: Record<
  CaseDisposition,
  { label: string; variant: "success" | "outline" | "warning" | "error" }
> = {
  submitted: { label: "Submitted", variant: "success" },
  draft: { label: "Saved for later", variant: "outline" },
  missing_information: { label: "Missing info", variant: "warning" },
  incomplete_documentation: { label: "Incomplete docs", variant: "warning" },
  needs_escalation: { label: "Needs escalation", variant: "error" },
};

/** The routing flags offered in the submit split-button's "flag & move on" menu. */
const FLAG_DISPOSITIONS: {
  value: Exclude<CaseDisposition, "submitted" | "draft">;
  label: string;
  icon: typeof AlertTriangle;
  color: string;
}[] = [
  {
    value: "missing_information",
    label: "Missing needed information",
    icon: HelpCircle,
    color: "var(--ch-blue-500)",
  },
  {
    value: "incomplete_documentation",
    label: "Incomplete documentation",
    icon: AlertTriangle,
    color: "var(--ch-amber-500)",
  },
  {
    value: "needs_escalation",
    label: "Needs escalation",
    icon: Flag,
    color: "var(--ch-coral-500)",
  },
];

/** Progress meter + the submit split-button (primary "Submit …" with a dropdown of
 *  save/flag dispositions). One instance, at the top of the field list. */
function SubmitControl({
  progress,
  submitLabel,
  submitTitle,
  submitting,
  onSubmit,
  onDisposition,
}: {
  progress: { done: number; total: number; complete: boolean };
  submitLabel: string;
  submitTitle: string;
  /** a submit is in flight — the case has not been recorded yet */
  submitting: boolean;
  onSubmit: () => void;
  onDisposition: (d: CaseDisposition) => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-2.5">
      <div className="flex w-24 flex-col gap-1">
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {progress.done} / {progress.total} fields
        </span>
        <ProgressBar
          value={progress.done}
          max={progress.total}
          tone={progress.complete ? "teal" : "amber"}
        />
      </div>
      <div className="flex items-stretch">
        <Button
          variant="primary"
          size="sm"
          onClick={onSubmit}
          // Disabled while in flight so a second click cannot append a second
          // review for the same case while the first is still unresolved.
          disabled={!progress.complete || submitting}
          className="rounded-r-none"
          title={submitTitle}
        >
          <Send /> {submitting ? "Submitting…" : submitLabel}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="primary"
              size="sm"
              className="rounded-l-none border-l border-white/25 px-1.5"
              aria-label="Save or flag this case"
              title="Save or flag this case"
            >
              <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuItem
              className="gap-2"
              onClick={() => onDisposition("draft")}
            >
              <Save className="size-4 text-muted-foreground" /> Save for later
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Flag &amp; move on
            </DropdownMenuLabel>
            {FLAG_DISPOSITIONS.map(({ value, label, icon: Icon, color }) => (
              <DropdownMenuItem
                key={value}
                className="gap-2"
                onClick={() => onDisposition(value)}
              >
                <Icon className="size-4" style={{ color }} /> {label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

type RowLayout = "side" | "stack";

function reviewDescriptor(caseId: string): BladeDescriptor {
  return {
    key: "audit-review",
    entityId: caseId,
    // NO eyebrow, and the bare case id as the title.
    //
    // The breadcrumb renders `eyebrow › title` per blade, so the batch blade
    // already contributes "AMGA › B-2026-0612-01". Repeating the job code here
    // produced "AMGA › B-2026-0612-01 › AMGA › Review AC-100482" — the client
    // twice and a verb where an identifier belongs. Dropping it leaves the chain
    // the reviewer actually navigates: job code › batch › episode.
    //
    // Safe because this blade is chromeless: the eyebrow had no other consumer,
    // and Breadcrumb guards on `blade.eyebrow &&`.
    title: caseId,
    width: "xwide",
    // The compare screen renders its own merged header (title + back + identity +
    // meta + actions), so suppress the shell blade-head to avoid a redundant title
    // line with dead space above it.
    chromeless: true,
  };
}

/** A resolved-verdict green check dot. */
function DoneDot() {
  return (
    <span
      className="inline-flex size-4 shrink-0 items-center justify-center rounded-full"
      style={{ background: "var(--ch-teal-500)" }}
    >
      <Check className="size-2.5" style={{ color: "#fff" }} aria-hidden />
    </span>
  );
}

/** The verdict's chosen correct code string (for the collapsed summary). */
function chosenCodeText(
  field: AuditField,
  a: CodedFields,
  b: CodedFields,
  verdict: VerdictId,
): string | null {
  const pick =
    verdict === "a"
      ? a[field.key]
      : verdict === "b"
        ? b[field.key]
        : verdict === "both"
          ? a[field.key]
          : null;
  if (pick == null) return null;
  return Array.isArray(pick) ? pick.join(", ") : String(pick);
}

/**
 * One SINGLE-value field's adjudication row. Correct toggles per slot (additive when
 * A===B → "both"; mutually exclusive when they differ), a "Neither is correct"
 * affordance that reveals the codebook picker, a reason select shown once a verdict
 * is chosen, and an optional note. Auto-collapses to a summary once resolved.
 */
function FieldCompareRow({
  field,
  a,
  b,
  layout,
  state,
  onSet,
  onOpenCode,
}: {
  field: AuditField;
  a: CodedFields;
  b: CodedFields;
  layout: RowLayout;
  state: SingleFieldVerdict | undefined;
  onSet: (patch: Partial<SingleFieldVerdict>) => void;
  onOpenCode: (code: string) => void;
}) {
  const matched = slotsMatch(field, a, b);
  const v = state?.verdict;
  const aC = v === "both" || v === "a";
  const bC = v === "both" || v === "b";
  const neither = v === "neither";
  const reasonOpt = reasonOptional(state, matched);
  const resolved = singleFieldResolved(state, matched);

  const [collapsed, setCollapsed] = useState(false);
  const [showNote, setShowNote] = useState(!!state?.note);
  // Auto-collapse a field once a verdict + reason (or a reason-optional match) lands.
  useEffect(() => {
    if (resolved) setCollapsed(true);
  }, [resolved]);

  const toggle = (which: "a" | "b") =>
    onSet({ verdict: toggleSingleSlot(v, which, matched) });
  const toggleNeitherSlot = () => onSet({ verdict: computeToggleNeither(v) });

  if (collapsed) {
    const reasonTxt = state?.reason
      ? reasonLabel(state.reason)
      : reasonOpt
        ? "Match — no reason needed"
        : "Not yet reviewed";
    const chosen = v ? chosenCodeText(field, a, b, v) : null;
    return (
      <div
        data-testid="compare-row"
        data-field={field.key}
        data-matched={matched}
        data-resolved={resolved}
        className={
          layout === "side"
            ? "border-b border-border py-2.5 last:border-0"
            : "rounded-coronis border border-border bg-card p-2.5"
        }
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            aria-label={`Expand ${field.label}`}
            className="inline-flex size-6 shrink-0 items-center justify-center rounded-coronis text-muted-foreground hover:bg-muted"
          >
            <ChevronRight className="size-3.5" />
          </button>
          {resolved && <DoneDot />}
          <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-foreground">
            {field.label}
          </span>
          {v ? (
            <span className="flex shrink-0 items-center gap-1.5">
              <VerdictChip verdict={v} />
              {chosen && (
                <span className="font-mono text-[11.5px] font-semibold text-foreground">
                  {chosen}
                </span>
              )}
              {state?.correctCodes && state.correctCodes.length > 0 && (
                <span
                  className="font-mono text-[11.5px] font-semibold"
                  style={{ color: "var(--chip-accent-fg)" }}
                >
                  → {state.correctCodes.join(", ")}
                </span>
              )}
            </span>
          ) : (
            <FieldStatusBadge field={field} a={a} b={b} />
          )}
          <span
            className="min-w-0 flex-1 truncate text-[11.5px] italic text-muted-foreground"
            title={reasonTxt}
          >
            {reasonTxt}
          </span>
          <NoteButton
            has={!!state?.note}
            open={showNote}
            onClick={() => setShowNote((s) => !s)}
          />
        </div>
        {showNote && (
          <div className="mt-2 pl-8">
            <FieldNote
              value={state?.note ?? ""}
              onChange={(note) => onSet({ note })}
            />
          </div>
        )}
      </div>
    );
  }

  // Field title group (chevron · name · help). The Match/Differs status capsule is
  // rendered as a sibling on the right of the header row (upper-right of the field
  // box), not stacked below the title.
  const titleGroup = (
    <span className="flex min-w-0 items-center gap-1.5">
      <button
        type="button"
        onClick={() => setCollapsed(true)}
        aria-label={`Collapse ${field.label}`}
        className="inline-flex size-6 shrink-0 items-center justify-center rounded-coronis text-muted-foreground hover:bg-muted"
      >
        <ChevronDown className="size-3.5" />
      </button>
      <span className="truncate text-[12.5px] font-semibold text-foreground">
        {field.label}
      </span>
      <FieldHelp field={field} />
    </span>
  );
  const header = (
    <div className="flex items-center justify-between gap-2">
      {titleGroup}
      <FieldStatusBadge field={field} a={a} b={b} />
    </div>
  );

  const slotRow = (slot: "a" | "b") => {
    const value = slot === "a" ? a[field.key] : b[field.key];
    const other = slot === "a" ? b[field.key] : a[field.key];
    const checked = slot === "a" ? aC : bC;
    return (
      <SlotRow slot={slot}>
        <CorrectToggle checked={checked} onChange={() => toggle(slot)} />
        <SrcTag slot={slot} />
        <span className="min-w-0 flex-1">
          <CompareValue
            field={field}
            slot={slot}
            value={value}
            other={other}
            onOpenCode={onOpenCode}
            forceMatched={checked}
          />
        </span>
      </SlotRow>
    );
  };

  const verdictControls = (
    <div className="flex min-w-0 flex-col gap-2">
      <NeitherToggle checked={neither} onChange={toggleNeitherSlot} />
      {neither && (
        <CorrectCodeEntry
          codes={state?.correctCodes}
          onChange={(cc) => onSet({ correctCodes: cc })}
          kind={field.kind}
          onOpenCode={onOpenCode}
        />
      )}
      {v ? (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <ReasonSelect
              field={field}
              value={state?.reason}
              onChange={(reason) => onSet({ reason })}
              placeholder={
                reasonOpt ? "Reason (optional)…" : "Reason for incorrect code…"
              }
            />
            <NoteButton
              has={!!state?.note}
              open={showNote}
              onClick={() => setShowNote((s) => !s)}
            />
          </div>
          {showNote && (
            <FieldNote
              value={state?.note ?? ""}
              onChange={(note) => onSet({ note })}
            />
          )}
        </div>
      ) : (
        <span className="text-[11.5px] italic text-muted-foreground">
          Select a verdict to add a reason
        </span>
      )}
    </div>
  );

  if (layout === "side") {
    return (
      <div
        data-testid="compare-row"
        data-field={field.key}
        data-matched={matched}
        data-resolved={resolved}
        className="grid items-start gap-x-3 border-b border-border py-3 last:border-0"
        style={{ gridTemplateColumns: SIDE_COLS }}
      >
        {header}
        {slotRow("a")}
        {slotRow("b")}
        <div data-testid="verdict-slot">{verdictControls}</div>
      </div>
    );
  }

  return (
    <div
      data-testid="compare-row"
      data-field={field.key}
      data-matched={matched}
      data-resolved={resolved}
      className="flex flex-col gap-2 rounded-coronis border border-border bg-card p-3 shadow-[var(--shadow-xs)]"
    >
      {header}
      <div className="flex flex-col gap-1.5">
        {slotRow("a")}
        {slotRow("b")}
      </div>
      <div data-testid="verdict-slot">{verdictControls}</div>
    </div>
  );
}

const MULTI_COLS = "24px minmax(0,1fr) minmax(0,1fr) 24px";

/**
 * One MULTI-value field's adjudication. A per-code union grid where each distinct
 * code has its own correct/flag checkbox (shared codes default correct; one-sided
 * codes default flagged), a per-code reason + note when flagged, plus auditor-added
 * correct codes with a why-add reason. Auto-collapses when every code is resolved.
 */
function MultiFieldCompareCard({
  field,
  a,
  b,
  state,
  onSet,
  onOpenCode,
}: {
  field: AuditField;
  a: CodedFields;
  b: CodedFields;
  state: MultiFieldVerdict | undefined;
  onSet: (patch: Partial<MultiFieldVerdict>) => void;
  onOpenCode: (code: string) => void;
}) {
  const matched = slotsMatch(field, a, b);
  const aCodes = (a[field.key] as string[]) ?? [];
  const bCodes = (b[field.key] as string[]) ?? [];
  const rows = multiUnionRows(aCodes, bCodes);
  const added = state?.added ?? [];
  const rowsState = state?.rows ?? {};
  const rowOf = (code: string) => rowsState[code] ?? {};
  const empty = rows.length === 0 && added.length === 0;
  const complete = multiFieldComplete(aCodes, bCodes, state);
  const flaggedRows = rows.filter((r) => !multiRowCorrect(r, rowOf(r.code)));
  const correctCount = rows.filter((r) =>
    multiRowCorrect(r, rowOf(r.code)),
  ).length;

  const setRow = (code: string, patch: Partial<MultiRowVerdict>) =>
    onSet({ rows: { ...rowsState, [code]: { ...rowOf(code), ...patch } } });
  const addCode = (code: string) => {
    if (!added.includes(code) && !rows.some((r) => r.code === code))
      onSet({ added: [...added, code] });
  };
  const removeCode = (code: string) => {
    const nr = { ...rowsState };
    delete nr[code];
    onSet({ added: added.filter((c) => c !== code), rows: nr });
  };

  const [collapsed, setCollapsed] = useState(false);
  const [showNote, setShowNote] = useState(!!state?.note);
  // Auto-collapse ONLY a fully-matching field (A and B coded the same set — nothing
  // to adjudicate). A field with any discrepancy stays open through the whole review:
  // marking which code is correct must not collapse the card out from under an
  // un-reasoned discrepancy. The auditor collapses it manually once every flagged
  // code has a reason. Seeded once (initial mount) so a later match->edit can't yank
  // an open card shut.
  const didAutoCollapse = useRef(false);
  useEffect(() => {
    if (!didAutoCollapse.current && matched && complete) {
      didAutoCollapse.current = true;
      setCollapsed(true);
    }
  }, [matched, complete]);

  if (collapsed) {
    const summary = empty
      ? "No codes on either side — nothing to review"
      : flaggedRows.length === 0 && added.length === 0
        ? "All codes match — no changes"
        : [
            flaggedRows.length ? `${flaggedRows.length} flagged` : null,
            added.length ? `${added.length} added` : null,
          ]
            .filter(Boolean)
            .join(" · ");
    return (
      <div
        data-testid="compare-row"
        data-field={field.key}
        data-matched={matched}
        data-resolved={complete}
        className="my-1.5 rounded-coronis border border-border bg-card p-2.5 shadow-[var(--shadow-xs)]"
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            aria-label={`Expand ${field.label}`}
            className="inline-flex size-6 shrink-0 items-center justify-center rounded-coronis text-muted-foreground hover:bg-muted"
          >
            <ChevronRight className="size-3.5" />
          </button>
          {complete && !empty && <DoneDot />}
          <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-foreground">
            {field.label}
          </span>
          {empty ? (
            <Badge variant="outline">None coded</Badge>
          ) : (
            <Badge variant={flaggedRows.length ? "warning" : "success"}>
              {correctCount} of {rows.length} correct
            </Badge>
          )}
          <span
            className="min-w-0 flex-1 truncate text-[11.5px] text-muted-foreground"
            title={summary}
          >
            {summary}
          </span>
          {added.length > 0 && (
            <span
              className="shrink-0 font-mono text-[11px] font-semibold"
              style={{ color: "var(--chip-accent-fg)" }}
            >
              +{added.join(", ")}
            </span>
          )}
          {!empty && (
            <NoteButton
              has={!!state?.note}
              open={showNote}
              onClick={() => setShowNote((s) => !s)}
            />
          )}
        </div>
        {showNote && (
          <div className="mt-2 pl-8">
            <FieldNote
              value={state?.note ?? ""}
              onChange={(note) => onSet({ note })}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="compare-row"
      data-field={field.key}
      data-matched={matched}
      data-resolved={complete}
      className="my-1.5 flex flex-col gap-2 rounded-coronis border border-border bg-card p-3 shadow-[var(--shadow-xs)]"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            aria-label={`Collapse ${field.label}`}
            className="inline-flex size-6 shrink-0 items-center justify-center rounded-coronis text-muted-foreground hover:bg-muted"
          >
            <ChevronDown className="size-3.5" />
          </button>
          <span className="text-[12.5px] font-semibold text-foreground">
            {field.label}
          </span>
          <FieldHelp field={field} />
        </span>
        <FieldStatusBadge field={field} a={a} b={b} />
      </div>

      {rows.length > 0 && (
        <div className="rounded-coronis bg-muted/40 p-1.5">
          <div
            className="grid items-center gap-x-2 border-b border-border pb-1"
            style={{ gridTemplateColumns: MULTI_COLS }}
          >
            <span aria-hidden />
            <SlotEyebrow slot="a" />
            <SlotEyebrow slot="b" />
            <span aria-hidden />
          </div>
          <div className="flex flex-col gap-1.5 pt-1.5">
            {rows.map((r) => (
              <MultiCodeRow
                key={r.code}
                field={field}
                row={r}
                rowState={rowOf(r.code)}
                correct={multiRowCorrect(r, rowOf(r.code))}
                onToggle={() =>
                  setRow(r.code, {
                    correct: !multiRowCorrect(r, rowOf(r.code)),
                  })
                }
                onSet={(patch) => setRow(r.code, patch)}
                onOpenCode={onOpenCode}
                renderCell={(code, cellMatched, slot, open) => (
                  <CodeCell
                    code={code}
                    matched={cellMatched}
                    slot={slot}
                    onOpenCode={open}
                  />
                )}
              />
            ))}
          </div>
        </div>
      )}

      {added.length > 0 && (
        <div className="flex flex-col gap-1">
          <span
            className="text-[10px] font-bold uppercase tracking-wider"
            style={{ color: "var(--chip-accent-fg)" }}
          >
            Auditor-added correct codes
          </span>
          {added.map((code) => (
            <AddedCodeRow
              key={code}
              field={field}
              code={code}
              rowState={rowOf(code)}
              onSet={(patch) => setRow(code, patch)}
              onRemove={() => removeCode(code)}
              onOpenCode={onOpenCode}
            />
          ))}
        </div>
      )}

      <AddCodeControl field={field} onAdd={addCode} />

      <p className="text-[11px] text-muted-foreground">
        Uncheck any code that is wrong and give a reason. Add codes the record
        supports but both results missed.
      </p>
    </div>
  );
}

/** One qualifier flag pill: the blind A/B tag + On/Off, tinted with the slot accent
 *  (blue = A, plum = B) when the flag is on, neutral when off — the prototype's
 *  FlagPill. The tint is a translucent composite so it reads on light and dark. */
function FlagPill({ slot, on }: { slot: "a" | "b"; on: boolean }) {
  const accent = SLOT_ACCENT[slot];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border py-0.5 pl-1 pr-2 transition-colors"
      style={{
        borderColor: on
          ? `color-mix(in srgb, ${accent.rail} 45%, transparent)`
          : "var(--border-default)",
        background: on ? accent.wash : "var(--bg-surface)",
      }}
    >
      <SlotTag slot={slot} />
      <span
        className="text-[11.5px] font-semibold"
        style={{ color: on ? accent.fg : "var(--fg-tertiary)" }}
      >
        {on ? "On" : "Off"}
      </span>
    </span>
  );
}

/** Anesthesia-qualifier adjudication strip. Agreeing flags show a status only;
 *  disagreeing flags get an on/off "which side is correct" control. */
function QualifierStrip({
  a,
  b,
  verdicts,
  onSetQ,
}: {
  a: CodedFields;
  b: CodedFields;
  verdicts: CaseVerdicts;
  onSetQ: (
    key: "emergency" | "unusual_position",
    patch: Partial<QualifierVerdict>,
  ) => void;
}) {
  return (
    <div className="mt-4 rounded-coronis border border-border bg-card p-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          Anesthesia qualifiers
        </h3>
        <span className="text-[11px] text-muted-foreground">
          On/off flags — compared A vs B
        </span>
      </div>
      <div className="mt-1.5 flex flex-col">
        {AUDIT_QUALIFIERS.map((q) => {
          const av = a[q.key];
          const bv = b[q.key];
          const agree = av === bv;
          const st = (verdicts["q_" + q.key] as QualifierVerdict) ?? {};
          return (
            <div
              key={q.key}
              data-testid="qualifier-row"
              data-agree={agree}
              data-resolved={agree || !!st.correct}
              className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border py-2.5"
            >
              <div className="flex w-[150px] shrink-0 flex-col">
                <span className="text-[12.5px] font-semibold text-foreground">
                  {q.label}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {q.sub}
                </span>
              </div>
              <FlagPill slot="a" on={av} />
              <FlagPill slot="b" on={bv} />
              <span
                className="ml-auto flex items-center gap-2"
                data-testid="qualifier-verdict-slot"
              >
                {agree ? (
                  <Badge variant={av ? "warning" : "outline"}>
                    {av ? "Both flag it" : "Neither flags it"}
                  </Badge>
                ) : (
                  <>
                    <Badge variant="error">Disagree</Badge>
                    <QualifierControl
                      value={st.correct}
                      onChange={(v) => onSetQ(q.key, { correct: v })}
                      label={q.label}
                    />
                  </>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Left pane — the anesthesia-record document viewer: THIS case's source PDF,
 *  fetched by its blob id and rendered on a PDF.js canvas (cross-browser, no
 *  native plugin dependency), with page navigation, zoom, and a pop-out
 *  fallback. A case with no stored document renders an explicit "no document"
 *  state; it must never fall back to the bundled sample, because adjudicating
 *  real codes against a synthetic record produces confident, meaningless
 *  verdicts. See ../sources/caseDocument.ts. */
function DocumentPlaceholder({
  c,
  onPopOut,
}: {
  c: AuditCase;
  onPopOut: () => void;
}) {
  return (
    <CaseDocumentPane
      className="flex h-full min-h-0 flex-col overflow-hidden rounded-coronis border border-border bg-card shadow-[var(--shadow-xs)]"
      documentBlobId={c.documentBlobId}
      mrn={c.mrn}
      procedure={c.record.procedure}
      onPopOut={onPopOut}
      headerAction={
        <button
          type="button"
          onClick={onPopOut}
          title="Open the record in a separate window"
          className="inline-flex items-center gap-1 rounded-coronis px-1.5 py-1 text-[11.5px] text-coronis-blue-200 transition-colors hover:bg-white/10 hover:text-white"
        >
          <ExternalLink className="size-3.5" />
          Pop out
        </button>
      }
    />
  );
}

/**
 * What the reviewer is told when a submit fails.
 *
 * Every branch says the same two things: it did NOT save, and the work is still
 * here. A reviewer who reads "something went wrong" next to a case that has
 * already advanced has no way to know which of those is true, and the rational
 * response — re-doing the case — is the one thing that would produce a
 * duplicate.
 */
function submitErrorMessage(err: unknown): string {
  if (err instanceof ConnectError) {
    if (
      err.code === Code.PermissionDenied ||
      err.code === Code.Unauthenticated
    ) {
      return "You don’t have permission to submit this case. Nothing was saved — your review is still here.";
    }
    if (err.code === Code.Unavailable || err.code === Code.DeadlineExceeded) {
      return "Couldn’t reach the server. Nothing was saved — your review is still here, try again.";
    }
  }
  return "This review wasn’t saved. Your work is still here — try submitting again.";
}

/** The failed-submit banner: the case has NOT been recorded and has not moved. */
function SubmitErrorBanner({
  message,
  onRetry,
  retrying,
}: {
  message: string;
  onRetry: () => void;
  retrying: boolean;
}) {
  return (
    <div
      role="alert"
      data-testid="submit-error"
      className="mt-2 flex flex-wrap items-center gap-3 rounded-coronis border border-[var(--border-error,var(--border-default))] bg-card px-3 py-2 shadow-[var(--shadow-xs)]"
    >
      <AlertTriangle className="size-4 shrink-0 text-[var(--fg-error,#b42318)]" />
      <span className="min-w-0 flex-1 text-[12.5px] text-foreground">
        <span className="font-semibold">Submit failed. </span>
        {message}
      </span>
      <Button variant="outline" size="sm" onClick={onRetry} disabled={retrying}>
        {retrying ? "Retrying…" : "Try again"}
      </Button>
    </div>
  );
}

/** Slim in-view banner shown while the document is open in a separate window.
 *  The document pane is hidden so the field comparison takes the full width;
 *  "Bring back into view" closes the popped window and restores the two-pane frame. */
function PoppedOutBanner({ onBringBack }: { onBringBack: () => void }) {
  return (
    <div className="flex items-center gap-3 rounded-coronis border border-dashed border-border bg-card px-4 py-3 shadow-[var(--shadow-xs)]">
      <ExternalLink className="size-4 shrink-0 text-coronis-blue-500" />
      <span className="text-[13px] text-foreground">
        The anesthesia record is open in a separate window.
      </span>
      <Button
        variant="outline"
        size="sm"
        className="ml-auto"
        onClick={onBringBack}
      >
        Bring back into view
      </Button>
    </div>
  );
}

/** A labelled identity column in the merged header. */
/**
 * One inline labelled fact for the identity line — "DOS 8/14/2026".
 *
 * Distinct from HeaderMeta, which stacks its label above its value in the grid.
 * These run WITH the patient identifiers on a single line, so the label sits
 * beside the value and is dimmed rather than capitalised: a row of small-caps
 * labels inline would compete with the patient's name for the eye.
 *
 * Renders nothing at all when there is no value. The grid's cells hold their
 * place because their neighbours align to them; an inline run has no such
 * obligation, and "Anes —" is noise where the field is simply absent.
 */
function HeaderFact({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <span className="whitespace-nowrap">
      <span className="text-muted-foreground/70">{label}</span>{" "}
      <span className="text-foreground">{value}</span>
    </span>
  );
}

function HeaderMeta({
  label,
  value,
  placeholder,
}: {
  label: string;
  value: string;
  /** Shown, muted, when the field has no value. Without one an empty field
   *  renders blank, which reads as a value that failed to load rather than as
   *  one the case genuinely does not have. */
  placeholder?: string;
}) {
  const empty = !value && !!placeholder;
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span
        className={`truncate text-xs ${empty ? "italic text-muted-foreground" : "text-foreground"}`}
        title={empty ? placeholder : value}
      >
        {empty ? placeholder : value}
      </span>
    </div>
  );
}

/**
 * Says why a case has nothing to compare.
 *
 * Most cases have one side. FA populates CODER_ID on roughly a tenth of
 * episodes at ingest because human coding lags episode creation, so a reviewer
 * opening a typical case sees "— not billed —" on every field of one column
 * with nothing on screen explaining it. That reads as a broken screen.
 *
 * # Deliberately slot-neutral
 *
 * It names the SLOT that is empty, never the source. Which side tends to be
 * missing is an inference the reviewer can already draw — the empty column is
 * right there — but the product must not confirm it for them, because the A/B
 * arrangement is stable per case and a confirmed pattern generalises to every
 * later case they open.
 *
 * # Why it warns rather than blocks
 *
 * A verdict on an uncomparable case is not a comparison, and the study cannot
 * use it. Blocking submission outright is the arguably-correct behaviour, but
 * it is a product decision with consequences for the reviewer's queue, so this
 * states the problem and leaves the choice.
 */
function UncomparableBanner({ c }: { c: AuditCase }) {
  if (c.hasA && c.hasB) return null;
  const missing = c.hasA ? "B" : "A";
  return (
    <div
      role="status"
      className="flex items-start gap-2.5 rounded-coronis border px-3.5 py-2.5"
      style={{
        borderColor: "color-mix(in srgb, var(--ch-amber-500) 40%, transparent)",
        background: "color-mix(in srgb, var(--ch-amber-500) 10%, var(--bg-surface))",
      }}
    >
      <AlertTriangle
        className="mt-0.5 size-4 shrink-0 text-coronis-amber-600"
        aria-hidden
      />
      <div className="flex min-w-0 flex-col">
        <span className="text-[12.5px] font-semibold text-coronis-amber-700">
          Only one coding exists for this case
        </span>
        <span className="text-[11.5px] text-muted-foreground">
          Result {missing} has not been coded, so there is nothing to compare
          against. A verdict recorded here measures no agreement and will not
          count toward the study.
        </span>
      </div>
    </div>
  );
}

function CompareBody({ ctx }: { ctx: BladeContext }) {
  const [fieldsRef, fieldsWidth] = useElementWidth(1000);
  const [bodyRef, bodyWidth] = useElementWidth(1200);
  const { status, data } = useAuditCase(ctx.descriptor.entityId);
  const { data: batch } = useAuditBatch(data?.batchId);
  const submitAudit = useAuditSubmit();

  // Walkthrough navigation: the review steps land here, and stepping BACK to a
  // batch step closes this blade to reveal the case list underneath.
  useTourNavigator((screen) => {
    if (screen === "review") return true;
    if (screen === "batch") {
      ctx.close();
      return true;
    }
    return false;
  });

  const caseId = data?.id;
  // Draft verdicts + general note, keyed on the blind case id. Re-seeded when the
  // case changes (prev/next/auto-advance swap the descriptor and remount is avoided,
  // so this effect reloads the draft for the new case).
  const [verdicts, setVerdicts] = useState<CaseVerdicts>({});
  const [generalNote, setGeneralNote] = useState("");
  const [codebookCode, setCodebookCode] = useState<string | null>(null);
  // Submit state. `submitError` is non-null only when the SERVER refused or was
  // unreachable — the case then stays exactly where it is, unmarked. It carries
  // the disposition that failed so Try again repeats THAT action: retrying a
  // failed "needs escalation" as a completed submit would record a judgement the
  // reviewer never made.
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<{
    message: string;
    disposition: CaseDisposition;
  } | null>(null);

  // Popped-out document window (HUB-504). When open, the in-view document pane is
  // hidden and the field comparison takes the full width. The window renders the
  // same styled document viewer (see AuditDocWindow), not a raw PDF.
  const [docPoppedOut, setDocPoppedOut] = useState(false);
  const docWinRef = useRef<Window | null>(null);

  const openDocWindow = () => {
    // URL carries only the blind case id — never MRN / name / payload (PHI hygiene).
    const url = `${import.meta.env.BASE_URL}audit-doc?case=${encodeURIComponent(caseId ?? "")}`;
    const win = window.open(url, "coronis-audit-doc", "width=940,height=1180");
    if (!win) return; // popup blocked — leave the pane in place rather than hide it.
    docWinRef.current = win;
    setDocPoppedOut(true);
    win.focus();
  };

  const bringDocBack = () => {
    docWinRef.current?.close();
    docWinRef.current = null;
    setDocPoppedOut(false);
  };

  // While popped out, watch for the user closing the window directly (its own
  // "Return to review" button, or the OS close control) and restore the pane.
  useEffect(() => {
    if (!docPoppedOut) return;
    const t = setInterval(() => {
      if (!docWinRef.current || docWinRef.current.closed) {
        docWinRef.current = null;
        setDocPoppedOut(false);
      }
    }, 600);
    return () => clearInterval(t);
  }, [docPoppedOut]);

  useEffect(() => {
    if (!caseId) return;
    setVerdicts(loadVerdicts(caseId));
    setGeneralNote(loadGeneralNote(caseId));
    // A failure belongs to the case it happened on; carrying it onto the next
    // one would blame a case nobody has submitted yet.
    setSubmitError(null);
    setSubmitting(false);
    // Switching cases (prev/next/auto-advance) closes any popped window so it can
    // never show a stale case's document; the pane returns for the new case.
    docWinRef.current?.close();
    docWinRef.current = null;
    setDocPoppedOut(false);
  }, [caseId]);

  // Close the popped window if the reviewer navigates away from the compare screen.
  useEffect(() => () => docWinRef.current?.close(), []);

  useEffect(() => {
    if (caseId) saveVerdicts(caseId, verdicts);
  }, [caseId, verdicts]);
  useEffect(() => {
    if (caseId) saveGeneralNote(caseId, generalNote);
  }, [caseId, generalNote]);

  const setField = (key: string, patch: object) =>
    setVerdicts((v) => ({ ...v, [key]: { ...(v[key] as object), ...patch } }));

  if (status === "loading") return <BladeLoading />;
  if (status === "error")
    return <BladeError message="Something went wrong loading this case." />;
  if (status === "denied")
    return <BladeDenied description="You don’t have access to this case." />;
  if (!data)
    return (
      <BladeEmpty
        title="Case not found"
        description="This case is no longer available."
      />
    );

  const c = data;
  const a = c.results.a;
  const b = c.results.b;
  const coder = coderById(c.coderId);

  const cases = batch?.cases ?? [];
  const idx = cases.findIndex((cc) => cc.id === c.id);
  const total = batch?.caseCount ?? cases.length;
  const prevCase = idx > 0 ? cases[idx - 1] : undefined;
  const nextCase =
    idx >= 0 && idx < cases.length - 1 ? cases[idx + 1] : undefined;
  const multiCase = cases.length > 1;

  const progress = computeProgress(a, b, verdicts);

  const goBack = () => ctx.close();
  const goto = (id: string) => ctx.replaceSelf(reviewDescriptor(id));

  // Next UNREVIEWED case in the batch after the current one (skipping already-
  // submitted cases, including ones submitted this session).
  const nextUnreviewed = (): AuditBatchCaseSummary | undefined => {
    if (idx < 0) return undefined;
    const submitted = loadSubmittedIds();
    for (let i = idx + 1; i < cases.length; i += 1) {
      const cc = cases[i];
      if (cc.status !== "submitted" && !submitted.has(cc.id)) return cc;
    }
    return undefined;
  };

  // Leave the case with a disposition, then advance. `submitted` is a completed
  // evaluation (gated on progress.complete); `draft` sets it aside to finish later;
  // the flags route it out of the queue without a completed evaluation.
  //
  // The server call is AWAITED and its failure is fatal to the whole step. It used
  // to be `void submitAudit(...)` followed immediately by recording the disposition
  // and advancing, so a PermissionDenied or a dropped connection looked exactly
  // like a successful submit: the reviewer saw the next case, the batch counted
  // this one as done (the batch view unions the local dispositions in), and the
  // work existed nowhere but this browser. On failure nothing is marked, nothing
  // advances, and the error is shown — the draft is already in localStorage, so
  // the reviewer can simply press Submit again.
  const finalize = async (disposition: CaseDisposition) => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitAudit({
        caseId: c.id,
        // Every compared field, both qualifiers, per-field notes and the per-code
        // adjudication of the multi-value fields — see buildFieldVerdicts, which
        // replaced a cast-and-read-.verdict loop that silently dropped all of it.
        fieldVerdicts: buildFieldVerdicts(a, b, verdicts),
        note: generalNote || undefined,
        disposition,
      });
    } catch (err) {
      setSubmitError({ message: submitErrorMessage(err), disposition });
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    setDisposition(c.id, disposition);
    // A completed submit skips ahead to the next UNREVIEWED case; save/flag just step
    // to the next case in the batch. Fall back to the batch when there's none left.
    const next =
      disposition === "submitted" ? (nextUnreviewed() ?? nextCase) : nextCase;
    if (next) {
      goto(next.id);
    } else {
      goBack();
    }
  };

  const handleSubmit = () => {
    if (!progress.complete) return;
    void finalize("submitted");
  };

  const rowLayout: RowLayout = fieldsWidth >= SIDE_THRESHOLD ? "side" : "stack";
  const panesStacked = bodyWidth < TWO_PANE_THRESHOLD;
  const caseDisposition: CaseDisposition | undefined =
    getDisposition(c.id) ??
    (c.status === "submitted" ? "submitted" : undefined);
  const dispositionBadge = caseDisposition
    ? DISPOSITION_BADGE[caseDisposition]
    : ({ label: "Unreviewed", variant: "info" } as const);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* Header: an action row (back · case nav · progress-grouped-with-submit), then
          the case identity, then a responsive meta grid so the fields stay aligned
          and reflow cleanly at narrow widths. */}
      <div className="flex shrink-0 flex-col gap-2 border-b border-border pb-2.5">
        {/* Action row: back · case id + status + N-of-N · prev/next. The submit control
            lives at the top of the field list, not here. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={goBack}
            title="Back to the batch"
            className="shrink-0"
          >
            <ChevronLeft />{" "}
            <span className="font-mono">
              {batch ? batch.batchNumber : "Batch"}
            </span>
          </Button>
          <span
            className="hidden h-5 w-px self-center bg-border sm:block"
            aria-hidden
          />
          {/* FA's episode id, or the DCN tagged as such. The DCN is the
              correlation key and stays in the meta grid below; it is not what a
              reviewer quotes — DOCUMENT_NUMBER is scoped per job, so the same
              number belongs to different patients across clients (HUB-633).

              Same tagged component as the case table, so the identity a
              reviewer clicked and the identity they land on cannot describe
              themselves differently. */}
          <CaseIdentifier
            className="text-xs font-semibold text-foreground"
            value={caseLabel(c)}
            isFallback={isFallbackLabel(c)}
            title={caseLabelTitle(c)}
          />
          <Badge variant={dispositionBadge.variant}>
            {dispositionBadge.label}
          </Badge>
          {multiCase && idx >= 0 && (
            <span className="text-[11px] text-muted-foreground">
              Case {idx + 1} of {total}
            </span>
          )}
          {multiCase && (
            <div className="ml-auto flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={() => prevCase && goto(prevCase.id)}
                disabled={!prevCase}
                aria-label="Previous case"
                title={
                  prevCase
                    ? `Previous case — ${prevCase.patientFull}`
                    : "First case in batch"
                }
              >
                <ChevronLeft />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={() => nextCase && goto(nextCase.id)}
                disabled={!nextCase}
                aria-label="Next case"
                title={
                  nextCase
                    ? `Next case — ${nextCase.patientFull}`
                    : "Last case in batch"
                }
              >
                <ChevronRight />
              </Button>
            </div>
          )}
        </div>

        {/* Patient identity.
            NO age and NO date of birth. FA redacts the birth date to a
            year-shifted value, so there is nothing true to show — and this
            line used to render "0y · DOB ·" from the empty fields, which reads
            as a patient with no age rather than as a value we do not hold.
            Sex and MRN arrive unredacted and are shown when present. */}
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <span className="text-[15px] font-semibold leading-tight text-foreground">
            {c.patient_context
              ? c.patientFull || "Name not recorded"
              : "Patient restricted"}
          </span>
          <span className="text-[11.5px] font-normal text-muted-foreground">
            {/* MRN and the patient ACCOUNT number. FA's coding screen shows the
                account number, and it is how a reviewer cross-references a case
                back into FA. Carried on the case context and previously
                unrendered.
                Unlabelled because a reviewer knows these three by shape — a sex
                code, a long MRN, a shorter account number. */}
            {[c.sex, c.mrn, c.patient_context?.accountNumber]
              .filter(Boolean)
              .join(" · ")}
          </span>

          {/* The ENCOUNTER — when, how, where — promoted out of the grid below.
              These describe the operation rather than the record, so they sit
              with the patient rather than among the client, payer and case
              identifiers.

              LABELLED, unlike the patient ids beside them. "8/14/2026" and
              "OR08" would read on their own, but the anesthesia type is a bare
              letter ('G' on most FA rows) that means nothing unprefixed — and
              labelling only the ambiguous one would read as an oversight on the
              others. */}
          <span className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[11.5px] text-muted-foreground">
            <HeaderFact label="DOS" value={c.dos} />
            <HeaderFact label="Anes" value={c.anesth} />
            {/* No Surgeon. FA's flattened episode query carries the anesthesia
                provider and CRNA of record and has no surgeon at all, so the
                field could only ever be blank. Room is real, from the same
                source. */}
            <HeaderFact label="Room" value={c.episode?.operatingRoom ?? ""} />
          </span>
        </div>

        {/* Context meta, in TWO GROUPS rather than one run of cells.
            Doug, 2026-08-26: the flat grid was not organised — it reflowed into
            whatever order the columns happened to fall in, which put the coder
            between the facility and the room, orphaned the anesthesia type at
            the start of a row, and split the two case identifiers across
            different rows.

            The split is by what the reviewer is asking:
              WHOSE WORK IS THIS — client, facility, payer. The billing context.
              WHICH RECORD IS THIS — episode id, DCN, coder. The case's identity
              and who coded it.

            Each group keeps its own three-column grid, so the members of a
            group stay aligned with each other and a long value (payer names run
            long; a DCN is fifteen digits) cannot push a neighbouring group's
            columns out of line.

            Side by side from lg, STACKED below it — and a grid rather than
            flex-wrap so which of those happens is decided by the breakpoint
            instead of by whether the content happened to fit. Two groups of
            three sharing a narrow header gives each cell about a third of half
            the width, which truncated "Anesthesia Assoc of Greater Miami" to
            "Anesthesia Ass…" and the payer to "UnitedHealthc…". Stacked, each
            cell gets a third of the FULL width and the values survive.

            lg (1024px) rather than xl: Doug, 2026-08-27, wanting the two groups
            on one line at his own window size. Note this is a VIEWPORT query
            while what actually governs the truncation is the width of this
            header — the two differ by the sidebar, which collapses. If that
            gap starts to matter, the honest fix is a container query on the
            header (Tailwind v4 has them built in) rather than a smaller
            viewport breakpoint.

            The identifier group is sized to its CONTENT (auto) and the billing
            group takes what is left, rather than splitting the header by a
            fixed ratio.

            Because the one thing here that must never be half-shown is an
            identifier. Measured at 1072px under a 3fr/2fr split: a real FA DCN
            ("612622905739001") needs 103px and had 90 — a reviewer copying a
            truncated DCN into FA finds nothing, or finds the wrong case. A
            client or payer name losing its tail is an ordinary table
            affordance with the full value on hover; a clipped identifier is a
            trap. So the squeeze lands on the free text, deliberately. */}
        <div className="grid grid-cols-1 gap-x-6 gap-y-2 lg:grid-cols-[minmax(0,1fr)_auto]">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-3">
            {/* Client comes from the BATCH, which this screen already loads —
                filling it from a second per-case lookup would be a redundant
                round trip on the case-open path. */}
            <HeaderMeta
              label="Client"
              value={[batch?.client, batch?.jobCode].filter(Boolean).join(" · ")}
            />
            <HeaderMeta label="Facility" value={c.facility} />
            <HeaderMeta
              label="Payer"
              value={
                c.patient_context
                  ? [c.payer, c.service].filter(Boolean).join(" · ")
                  : "Restricted"
              }
            />
          </div>

          {/* A rule, not a gap. The two groups are the same size and weight, so
              spacing alone reads as an accident of layout rather than as a
              division.

              Carried by the second group as a left border rather than by a
              separate element, and turned on at the SAME breakpoint that puts
              the groups side by side. A standalone divider would become its own
              grid item once they stacked, and sit across the layout. */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-3 lg:border-l lg:border-border lg:pl-6">
            {/* The two identifiers, ADJACENT and each LABELLED.

                The action row above leads with the episode id as the case's
                identity, but a bare number at the top of a screen does not say
                which number it is — and these two are both long digit strings
                that a reviewer has no way to tell apart by shape. Labelling
                them side by side is what makes the cross-reference unambiguous.

                Both are needed and neither substitutes: EPISODE_ID is what FA's
                own screens show (episodeDAO.java:1204), so it is the only one a
                reviewer can look a case up by; the DCN is what this system
                correlates on and what any log line or support request names.

                The episode id shown here is always the case's ONLY one. A DCN
                claimed by more than one FA episode is quarantined server-side
                and cannot be opened at all (AmbiguousDCN — see the exclusions
                in evalreview.Service), so this can never be one of several
                presented as though it were authoritative. */}
            <HeaderMeta
              label="FA Episode ID"
              value={c.episodeId}
              // A PDF-only case has no FA episode. Said plainly rather than
              // left blank, which reads as a field that failed to load.
              placeholder="No FA episode yet"
            />
            <HeaderMeta label="DCN" value={c.id} />
            {/* The coder belongs with the record, not with the encounter: it
                answers "who produced this coding", which is what the reviewer
                is judging. */}
            <HeaderMeta
              label="Coder"
              value={c.coderId ? coder.name : "Not coded yet"}
            />
          </div>
        </div>
      </div>

      <UncomparableBanner c={c} />

      {/* Two-pane frame: document viewer | field-by-field adjudication. In the
          side-by-side layout the document pane fills the full column height and
          stays put while the field list scrolls independently on the right. */}
      <div
        ref={bodyRef}
        className={
          panesStacked || docPoppedOut
            ? "flex min-h-0 flex-1 flex-col gap-4"
            : "grid min-h-0 flex-1 gap-4"
        }
        style={
          panesStacked || docPoppedOut
            ? undefined
            : { gridTemplateColumns: "minmax(0,2fr) minmax(0,1fr)" }
        }
      >
        {docPoppedOut ? (
          <PoppedOutBanner onBringBack={bringDocBack} />
        ) : (
          <div className={panesStacked ? "min-h-[440px]" : "min-h-0"}>
            <DocumentPlaceholder c={c} onPopOut={openDocWindow} />
          </div>
        )}

        <div
          ref={fieldsRef}
          className={
            panesStacked || docPoppedOut
              ? "flex min-w-0 flex-col"
              : "flex min-h-0 min-w-0 flex-col overflow-y-auto pr-1"
          }
        >
          {/* Sticky field-list header: title + the single progress/submit control, so it
              stays reachable while the field list scrolls. Wraps (control drops below the
              title) when the column is too narrow to hold both on one line. */}
          <div
            data-tour="tour-fields"
            className="sticky top-0 z-10 -mr-1 mb-2 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border py-2 pr-1"
            style={{ background: "var(--bg-app)" }}
          >
            <h3 className="text-[13px] font-semibold text-foreground">
              Field-by-field comparison
            </h3>
            <div data-tour="tour-submit" className="ml-auto">
              <SubmitControl
                progress={progress}
                submitting={submitting}
                submitLabel={nextUnreviewed() ? "Submit & next" : "Submit"}
                submitTitle={
                  !progress.complete
                    ? `Review all ${progress.total} fields before submitting`
                    : nextUnreviewed()
                      ? "Submit & go to next case"
                      : "Submit feedback"
                }
                onSubmit={handleSubmit}
                onDisposition={(d) => void finalize(d)}
              />
            </div>
            {submitError && (
              <div className="w-full">
                <SubmitErrorBanner
                  message={submitError.message}
                  onRetry={() => void finalize(submitError.disposition)}
                  retrying={submitting}
                />
              </div>
            )}
          </div>

          {rowLayout === "side" && (
            <div
              className="grid gap-x-3 border-b-2 border-border pb-1.5"
              style={{ gridTemplateColumns: SIDE_COLS }}
            >
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Field
              </span>
              <SlotEyebrow slot="a" />
              <SlotEyebrow slot="b" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Reason &amp; note
              </span>
            </div>
          )}

          <div
            data-tour="tour-fieldcards"
            className={
              rowLayout === "side"
                ? "flex flex-col"
                : "flex flex-col gap-2 pt-1"
            }
          >
            {AUDIT_FIELDS.map((f) =>
              f.multi ? (
                <MultiFieldCompareCard
                  key={f.key}
                  field={f}
                  a={a}
                  b={b}
                  state={verdicts[f.key] as MultiFieldVerdict}
                  onSet={(patch) => setField(f.key, patch)}
                  onOpenCode={setCodebookCode}
                />
              ) : (
                <FieldCompareRow
                  key={f.key}
                  field={f}
                  a={a}
                  b={b}
                  layout={rowLayout}
                  state={verdicts[f.key] as SingleFieldVerdict}
                  onSet={(patch) => setField(f.key, patch)}
                  onOpenCode={setCodebookCode}
                />
              ),
            )}
          </div>

          <div data-tour="tour-qualifiers">
            <QualifierStrip
              a={a}
              b={b}
              verdicts={verdicts}
              onSetQ={(key, patch) => setField("q_" + key, patch)}
            />
          </div>

          {/* General case note */}
          <div className="mt-4 rounded-coronis border border-border bg-card p-3 shadow-[var(--shadow-xs)]">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                General case notes
              </span>
              <span className="text-[11px] text-muted-foreground">
                Applies to the whole case
              </span>
            </div>
            <Textarea
              className="min-h-[72px] text-sm"
              placeholder="Overall observations about this case, patterns in A vs B disagreement, documentation gaps…"
              value={generalNote}
              onChange={(e) => setGeneralNote(e.target.value)}
            />
          </div>
        </div>
      </div>

      {codebookCode && (
        <CodebookModal
          code={codebookCode}
          onClose={() => setCodebookCode(null)}
        />
      )}
    </div>
  );
}

export const auditComparePrimitive: ViewPrimitive = {
  key: "audit-review",
  defaultTitle: "Case review",
  defaultEyebrow: "Code evaluation",
  defaultWidth: "xwide",
  // Renders its own merged header (title + back + identity + meta + actions).
  chromeless: true,
  render: (ctx) => {
    const forced = forcedBladeState(ctx.descriptor);
    if (forced === "loading") return <BladeLoading />;
    if (forced === "error") return <BladeError />;
    if (forced === "denied") return <BladeDenied />;
    if (forced === "empty")
      return (
        <BladeEmpty
          title="Case not found"
          description="This case is no longer available."
        />
      );
    return <CompareBody ctx={ctx} />;
  },
};
